import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';

@Directive({
  selector: 'nz-radio-group',
  standalone: true,
})
export class NzRadioGroupAccessibilityDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;

    if (!host.hasAttribute('role')) {
      host.setAttribute('role', 'radiogroup');
    }

    this.syncTabIndexes('init');

    this.observer = new MutationObserver(() => {
      this.syncTabIndexes('mutation');
    });

    this.observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-checked'],
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  @HostListener('change')
  onChange(): void {
    this.syncTabIndexes('change');
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.syncTabIndexes('focusin');
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isNavigationKey(event.key)) {
      return;
    }

    const radioInputs = this.getEnabledRadioInputs();
    if (radioInputs.length === 0) {
      return;
    }

    const currentIndex = this.getCurrentIndex(radioInputs);
    const nextIndex = this.getNextIndex(currentIndex, radioInputs.length, event.key);
    const nextInput = radioInputs[nextIndex];

    event.preventDefault();
    event.stopPropagation();

    if (!nextInput.checked) {
      nextInput.click();
    }

    nextInput.focus();
    this.syncTabIndexes('keydown');
  }

  private syncTabIndexes(sync: any): void {
    console.log('syncTabIndexes', sync);
    const radioInputs = this.getEnabledRadioInputs();
    if (radioInputs.length === 0) {
      return;
    }

    const checkedIndex = radioInputs.findIndex((input) => input.checked);
    const activeIndex = checkedIndex >= 0 ? checkedIndex : 0;

    radioInputs.forEach((input, index) => {
      const tabIndex = index === activeIndex ? 0 : -1;
      if (input.tabIndex !== tabIndex) {
        input.tabIndex = tabIndex;
      }
    });
  }

  private getEnabledRadioInputs(): HTMLInputElement[] {
    const inputs = Array.from(this.el.nativeElement.querySelectorAll('input[type="radio"]'));
    return inputs.filter((input): input is HTMLInputElement => input instanceof HTMLInputElement && !input.disabled);
  }

  private getCurrentIndex(radioInputs: HTMLInputElement[]): number {
    const activeElement = document.activeElement as HTMLElement | null;
    const focusedIndex = radioInputs.findIndex((input) => {
      const wrapper = input.closest('.ant-radio-wrapper');
      return input === activeElement || (!!wrapper && wrapper.contains(activeElement));
    });

    if (focusedIndex >= 0) {
      return focusedIndex;
    }

    const checkedIndex = radioInputs.findIndex((input) => input.checked);
    return checkedIndex >= 0 ? checkedIndex : 0;
  }

  private getNextIndex(currentIndex: number, total: number, key: string): number {
    if (key === 'Home') {
      return 0;
    }

    if (key === 'End') {
      return total - 1;
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      return currentIndex === 0 ? total - 1 : currentIndex - 1;
    }

    return currentIndex === total - 1 ? 0 : currentIndex + 1;
  }

  private isNavigationKey(key: string): boolean {
    return key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown' || key === 'Home' || key === 'End';
  }
}
